import React, { type ComponentProps } from 'react';
import { StyleSheet, useWindowDimensions, View } from 'react-native';

import { Footer as HTMLFooter } from '@expo/html-elements';

import { Container, HStack, theme, useNavbarContainerHeight, useResponsive } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';

import { useOrganizationLinksFooterContentfulData } from '../../hooks/useOrganizationLinksContentfulData';
import { OrganizationLinksFooterCopyright } from './components/OrganizationLinksFooterCopyright';
import { OrganizationLinksFooterSection } from './components/OrganizationLinksFooterSection';
import { useConditionalLinksRules } from './hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const OrganizationLinksFooter = (props: Props) => {
  const { style } = props;

  const contentfulFooter = useOrganizationLinksFooterContentfulData();
  const conditionalLinksRules = useConditionalLinksRules();
  const isNavbarV2Enabled = useFeatureFlag('CELS-5440-order-navbar-v2-enabled');

  const { width: viewportWidth } = useWindowDimensions();
  const { paddingBottomStyle: navbarPaddingBottomStyle } = useNavbarContainerHeight();
  const { currentBreakpoint, minWidth, match } = useResponsive();

  // ─── Derived ───────────────────────────────────────────────────────────────

  const shouldApplyNavbarPadding = isNavbarV2Enabled && currentBreakpoint.isXS;
  const itemsPerRow = viewportWidth >= 1280 ? undefined : match([1, 2, 4]);
  const palette = isNavbarV2Enabled ? 'dark' : 'light';

  // ─── Styles ────────────────────────────────────────────────────────────────

  const linkGroupsGap = match([theme.spacing['6'], theme.spacing['6'], theme.spacing['10']]);
  const containerStyles = [styles.container, minWidth.isMD ? styles.containerMD : undefined];
  const linkGroupsStackStyles = minWidth.isMD ? styles.linkGroupsStackMD : undefined;

  const footerStyles = [
    styles.footer,
    isNavbarV2Enabled ? styles.footerNavbarV2 : undefined,
    shouldApplyNavbarPadding ? navbarPaddingBottomStyle : undefined,
    style,
  ];

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <HTMLFooter style={footerStyles}>
      <Container style={containerStyles}>
        <View style={linkGroupsStackStyles}>
          <HStack itemsPerRow={itemsPerRow} gap={linkGroupsGap}>
            {contentfulFooter.sections.map((section) => (
              <OrganizationLinksFooterSection
                key={section.name}
                section={section}
                conditionalLinksRules={conditionalLinksRules}
                palette={palette}
              />
            ))}
          </HStack>
        </View>

        <OrganizationLinksFooterCopyright palette={palette} />
      </Container>
    </HTMLFooter>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  footer: {
    marginTop: 'auto',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.SKY,
    paddingVertical: theme.spacing['10'],
  },
  footerNavbarV2: {
    backgroundColor: theme.colors.BROWN,
  },
  container: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: theme.spacing['6'],
  },
  containerMD: {
    gap: theme.spacing['8'],
  },

  linkGroupsStackMD: {
    flex: 1,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  style?: ComponentProps<typeof HTMLFooter>['style'];
};
